/**
 * API client for core.u.software
 * Uses session cookies for authentication (credentials: 'include')
 *
 * For org-centric mode (core.{org}.u.software), uses api.{org}.u.software
 * For development, uses VITE_API_URL or localhost
 */

// Detect if we're running in org-centric mode
function getOrgFromHost(): string | null {
  const hostname = window.location.hostname
  // Production pattern: core.{slug}.u.software
  const prodMatch = hostname.match(/^core\.(.+)\.u\.software$/)
  if (prodMatch) return prodMatch[1]
  // Dev: use query param
  const params = new URLSearchParams(window.location.search)
  return params.get('org')
}

// Get the API URL based on org context
// Exported so other files can call this dynamically at request time
export function getApiUrl(): string {
  const org = getOrgFromHost()

  // Production org-centric mode
  if (org && window.location.hostname.includes('.u.software')) {
    return `https://api.${org}.u.software`
  }

  // Development or non-org mode
  return import.meta.env.VITE_API_URL || 'http://localhost:8080'
}

// Export for use in other files
export const API_URL = getApiUrl()

// Check if in org-centric mode
export const isOrgMode = (): boolean => getOrgFromHost() !== null

export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message)
    this.name = 'APIError'
  }
}

export async function apiFetch<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  // Compute API URL at request time to ensure correct org detection
  const apiUrl = getApiUrl()
  const res = await fetch(`${apiUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!res.ok) {
    let message = 'An error occurred'
    let code: string | undefined

    try {
      const error = await res.json()
      message = error.detail || error.message || message
      code = error.code
    } catch {
      // Response wasn't JSON
    }

    throw new APIError(message, res.status, code)
  }

  // Handle empty responses (204 No Content)
  if (res.status === 204) {
    return {} as T
  }

  return res.json()
}

// Get auth path prefix based on mode
// In org mode: /org-auth (for OrgUsers)
// In non-org mode: /api/auth (for Users - main u.software IdP)
function getAuthPrefix(): string {
  return isOrgMode() ? '/org-auth' : '/api/auth'
}

// Auth API - uses appropriate prefix based on org mode
export const auth = {
  register: (data: { email: string; password: string; display_name?: string }) =>
    apiFetch<{ user_id: string; email: string; message: string }>(`${getAuthPrefix()}/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (data: { email: string; password: string; remember_me?: boolean }) =>
    apiFetch<{
      user_id: string
      session_id: string
      email: string
      email_verified: boolean
      display_name: string | null
      mfa_required: boolean
      device_verification_required: boolean
      device_verification_methods: string[]
      device_id: string | null
    }>(`${getAuthPrefix()}/login`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  checkEmail: (email: string) =>
    apiFetch<{
      exists: boolean
      has_password: boolean
      providers: string[]
      avatar_url: string | null
    }>(`${getAuthPrefix()}/check-email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  sendDeviceVerificationEmail: () =>
    apiFetch<{ message: string }>(`${getAuthPrefix()}/device-verify/email`, {
      method: 'POST',
    }),

  verifyDevice: (data: { method: string; code: string; trust_device?: boolean }) =>
    apiFetch<{ success: boolean; message: string }>(`${getAuthPrefix()}/device-verify`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getDeviceVerificationMethods: () =>
    apiFetch<{ methods: string[] }>(`${getAuthPrefix()}/device-verify/methods`),

  logout: () =>
    apiFetch<{ message: string }>(`${getAuthPrefix()}/logout`, {
      method: 'POST',
    }),

  getSession: () =>
    apiFetch<{
      id: string
      user_id: string
      auth_method: string
      auth_level: string
      created_at: string
      expires_at: string
      is_current: boolean
    }>(`${getAuthPrefix()}/session`),

  refresh: () =>
    apiFetch<{ message: string }>(`${getAuthPrefix()}/refresh`, {
      method: 'POST',
    }),

  sendVerificationEmail: () =>
    apiFetch<{ message: string }>(`${getAuthPrefix()}/verify-email/send`, {
      method: 'POST',
    }),

  verifyEmail: (code: string) =>
    apiFetch<{ message: string }>(`${getAuthPrefix()}/verify-email/confirm`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  forgotPassword: (email: string) =>
    apiFetch<{ message: string }>(`${getAuthPrefix()}/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, new_password: string) =>
    apiFetch<{ message: string }>(`${getAuthPrefix()}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
    }),

  // Social auth - in org mode uses /org-auth/oauth/{provider}/redirect
  getSocialRedirect: (provider: string, returnTo?: string) => {
    const prefix = isOrgMode() ? '/org-auth/oauth' : '/api/auth'
    const path = isOrgMode()
      ? `${prefix}/${provider}/redirect`
      : `${prefix}/${provider}/redirect`
    return apiFetch<{ redirect_url: string; provider: string }>(
      `${path}${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''}`
    )
  },

  telegramCallback: (data: {
    id: number
    first_name: string
    last_name?: string
    username?: string
    photo_url?: string
    auth_date: number
    hash: string
    return_to?: string
  }) =>
    apiFetch<{
      user_id: string
      session_id: string
      email: string | null
      email_verified: boolean
      display_name: string | null
      is_new_user: boolean
      needs_profile_completion: boolean
      redirect_to: string | null
    }>(`${getAuthPrefix()}/telegram/callback`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  confirmLink: (linkingToken: string, provider: string) =>
    apiFetch<{
      user_id: string
      session_id: string
      identity_id: string
      message: string
    }>(`${getAuthPrefix()}/link/confirm`, {
      method: 'POST',
      body: JSON.stringify({ linking_token: linkingToken, provider }),
    }),
}

// Identity API
export const identity = {
  list: () =>
    apiFetch<{
      identities: Array<{
        id: string
        provider: string
        provider_user_id: string
        email: string | null
        email_verified: boolean
        display_name: string | null
        avatar_url: string | null
        created_at: string
        updated_at: string
      }>
    }>('/api/me/identities'),

  unlink: (identityId: string) =>
    apiFetch<{ message: string }>(`/api/me/identities/${identityId}`, {
      method: 'DELETE',
    }),
}

// User API
export const user = {
  getProfile: () =>
    apiFetch<{
      user: {
        id: string
        email: string | null
        email_verified: boolean
        username: string | null
        display_name: string | null
        avatar_url: string | null
        mfa_enabled: boolean
        system_role: string
        status: string
        created_at: string
        updated_at: string
        last_login_at: string | null
      }
    }>('/api/me'),

  updateProfile: (data: { display_name?: string; avatar_url?: string }) =>
    apiFetch<{ user: unknown; message: string }>('/api/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  setUsername: (username: string) =>
    apiFetch<{ username: string; message: string }>('/api/me/username', {
      method: 'POST',
      body: JSON.stringify({ username }),
    }),

  changePassword: (current_password: string, new_password: string) =>
    apiFetch<{ message: string }>('/api/me/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password, new_password }),
    }),
}

// OAuth API (for consent flow)
export interface ScopeInfo {
  name: string
  description: string
}

export interface AppInfo {
  id: string
  name: string
  logo_url: string | null
  homepage_url: string | null
}

export interface UserInfoCompact {
  id: string
  email: string | null
  display_name: string | null
}

export interface AuthorizeContext {
  app: AppInfo
  user: UserInfoCompact
  requested_scopes: ScopeInfo[]
  new_scopes: string[]
  already_consented: boolean
  redirect_uri: string
  state: string | null
  code_challenge: string | null
  code_challenge_method: string
  nonce: string | null
}

// Get OAuth base URL - uses dynamic API URL in production, same-origin in development
function getOAuthBaseUrl(): string {
  // In production org mode, use API URL directly
  if (isOrgMode() && window.location.hostname.includes('.u.software')) {
    return getApiUrl()
  }
  // In development, use same-origin (goes through Vite proxy)
  return ''
}

export const oauth = {
  getAuthorizeContext: async (params: {
    client_id: string
    redirect_uri: string
    scope: string
    state?: string
    code_challenge?: string
    code_challenge_method?: string
    nonce?: string
  }) => {
    const searchParams = new URLSearchParams()
    searchParams.set('client_id', params.client_id)
    searchParams.set('redirect_uri', params.redirect_uri)
    searchParams.set('scope', params.scope)
    if (params.state) searchParams.set('state', params.state)
    if (params.code_challenge) searchParams.set('code_challenge', params.code_challenge)
    if (params.code_challenge_method) searchParams.set('code_challenge_method', params.code_challenge_method)
    if (params.nonce) searchParams.set('nonce', params.nonce)

    const baseUrl = getOAuthBaseUrl()
    const res = await fetch(`${baseUrl}/oauth/authorize/context?${searchParams.toString()}`, {
      credentials: 'include',
    })
    if (!res.ok) {
      const error = await res.json().catch(() => ({}))
      throw new APIError(error.detail || 'Failed to get authorize context', res.status, error.code)
    }
    return res.json()
  },

  submitConsent: (params: {
    client_id: string
    redirect_uri: string
    scope: string
    approved_scopes: string
    state?: string
    code_challenge?: string
    code_challenge_method?: string
    nonce?: string
  }) => {
    const formData = new URLSearchParams()
    formData.set('client_id', params.client_id)
    formData.set('redirect_uri', params.redirect_uri)
    formData.set('scope', params.scope)
    formData.set('approved_scopes', params.approved_scopes)
    if (params.state) formData.set('state', params.state)
    if (params.code_challenge) formData.set('code_challenge', params.code_challenge)
    if (params.code_challenge_method) formData.set('code_challenge_method', params.code_challenge_method)
    if (params.nonce) formData.set('nonce', params.nonce)

    const baseUrl = getOAuthBaseUrl()
    return fetch(`${baseUrl}/oauth/authorize/consent`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    })
  },

  getAppInfo: async (clientId: string): Promise<AppInfo | null> => {
    try {
      const baseUrl = getOAuthBaseUrl()
      const res = await fetch(`${baseUrl}/oauth/app-info?client_id=${encodeURIComponent(clientId)}`, {
        credentials: 'include',
      })
      if (!res.ok) return null
      return res.json()
    } catch {
      return null
    }
  },
}

// Org API (for multi-tenant support)
export interface OrgBrandingConfig {
  logo_url: string | null
  favicon_url: string | null
  primary_color: string | null
  secondary_color: string | null
  custom_css: string | null
  show_powered_by: boolean
}

export interface OrgRegistrationConfig {
  mode: 'open' | 'invite_only' | 'domain_restricted'
  allowed_domains: string[]
}

export interface OrgOAuthConfig {
  managed: boolean
  providers: ('google' | 'github' | 'discord' | 'twitter' | 'telegram')[]
}

export interface OrgPublicConfig {
  slug: string
  name: string
  branding?: OrgBrandingConfig
  registration?: OrgRegistrationConfig
  oauth?: OrgOAuthConfig
}

export const org = {
  /**
   * Get public org configuration for multi-tenant branding
   * This endpoint is public and does not require authentication
   * Org is determined from the subdomain (api.{org}.u.software)
   */
  getPublicConfig: () =>
    apiFetch<OrgPublicConfig>(`/api/org/public-config`),

  /**
   * Validate invite code for invite-only registration
   */
  validateInviteCode: (slug: string, code: string) =>
    apiFetch<{ valid: boolean; email?: string }>(`/api/orgs/${slug}/validate-invite`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /**
   * Check if email domain is allowed for domain-restricted registration
   */
  checkEmailDomain: (slug: string, email: string) =>
    apiFetch<{ allowed: boolean; domain: string }>(`/api/orgs/${slug}/check-email-domain`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
}
